/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import JimJobInfo from '../../../shared/jim/jimJobInfo.component';
import { useAddJob } from '../../../shared/jim/hooks';
import Config from '../../../config';
import ViewGrid from '../../shared/viewGrid.component';

import HamburgerDropdown from '../../shared/hamburgerDropdown.component';
import columnDefinitions from '../../../shared/columnDefinitions/connections';
import Button from '../../../shared/button.component';

import PeriodDropdown from '../../../shared/periodDropdown.component';
import SecondaryMenu from '../../../menu/secondaryMenu.component';

import AddConnectionModal from './addConnectionModal.component';
import ConnectionSettingsAdminColumnRendererFactory from './connectionSettingsAdminColumnRenderer.component';
import ConnectionByTypeDropdown from '@client-shared/connectionByTypeDropdown.component';
import CalculationDropdown from '@client-shared/calculationDropdown.component';
import { useAdminConnections, useLookUpDataForAddEditConnectionModal } from '../../../shared/queries/connections';
import { useAddConnection, useUpdateConnection } from '../../../shared/mutations/clientAdministration';
import { fetchConnectionsByType } from '@client-shared/store/actions';
import { errorNotification, infoNotification, successNotification } from '@client-shared/store/notificationActions';
import { generateJournalPayload, fetchHistoricalTrialBalanceData } from './helper';
import Select from '../../../shared/reactSelect/select.component';
import { renderDate } from '../../../shared/dateHelpers';
import { fetchTrialBalanceCalculationId } from '../../../shared/api';
import downloadTrialBalanceFileXlsx from '../../common/connections/downloadTrialBalanceFileXlsx';

import {
  periodOrCurrentSelector,
  connectionByTypeSelector,
  calculationSelector,
  generateJournalFlagSelector,
  environmentSuffixSelector,
  connectionsByTypeOptionsSelector,
} from '../../../shared/store/selectors';

import { PeriodPropTypes } from '../../../shared/propTypes/period';
import { CalculationIdPropTypes } from '../../../shared/propTypes/calculation';
import { Connection, ConnectionFormData, SaveEntityFunction } from '../types';

import agGridConfig from '../../../agGridConfig';
import useSelectAgGridRow from '../../../shared/hooks/useSelectAgGridRow.hook';
import { TabHeader } from '../shared/tabHeader.component';

import useAddEditModalControls from '../shared/useAddEditModalControls';
import { Period, Connection as ConnectionType, CalculationId as CalculationIdType } from '../../../shared/types';
import { useGenerateJournalJob } from './useGenerateJournalJob.hook';
import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../../userData/store/selectors';


type ConnectionsTabProps = {
  period: Period,
  canModify: boolean,
  connection: ConnectionType,
  calculationId: CalculationIdType,
  connectionsByType: any[],
  lastClientId: string,
  sso: string,
  generateJournalFlag: boolean,
  hideDecimals: boolean,
  shouldDisplayZero: boolean,
  environmentSuffix: string,
  negativeMinus: boolean,
}

const ConnectionsTab: React.FC<ConnectionsTabProps> = ({
  period, canModify, connection, calculationId, connectionsByType, lastClientId, generateJournalFlag,
  environmentSuffix, shouldDisplayZero, hideDecimals, negativeMinus,
}) => {
  const dispatch = useDispatch();
  const [isGeneratingTrialBalance, setIsGeneratingTrialBalance] = React.useState(false);
  const [trialBalanceConnection, setTrialBalanceConnection] = React.useState<{
    label: string;
    value: string;
  } | null>(null);
  const [trialBalanceCalculationId, setTrialBalanceCalculationId] = React.useState<{
    label: string;
    value: string;
  } | null>(null);
  const [trialBalanceCalculations, setTrialBalanceCalculations] = React.useState<any[]>([]);
  const [isLoadingTrialBalanceCalculations, setIsLoadingTrialBalanceCalculations] = React.useState(false);

  const { data: connections, isFetching: isLoadingConnections } = useAdminConnections({ periodName: period.name });
  const { data: lookUpData, isFetching: isLoadingLookUpData } = useLookUpDataForAddEditConnectionModal();

  const { selectAgGridRow } = useSelectAgGridRow(agGridConfig.adminClientAdministrationConnections);
  const connType = 'FILE:FTP';

  useEffect(() => {
    dispatch(fetchConnectionsByType(period, connType));
  }, [dispatch, period]);

  useEffect(() => {
    if (connectionsByType && connectionsByType.length > 0 && !trialBalanceConnection) {
      const firstConnection = connectionsByType[0];
      setTrialBalanceConnection({
        label: firstConnection.label,
        value: firstConnection.value,
      });
    }
  }, [connectionsByType, trialBalanceConnection]);

  const {
    entityToEdit,
    showModalForEdit,
    showModalForAdd,
    hideModalForAddEdit,
    isAddEditModalVisible,
  } = useAddEditModalControls<Connection>();

  const adminColumnRenderer = useMemo(
    () => ConnectionSettingsAdminColumnRendererFactory({ showModalForEdit })
    , [showModalForEdit]
  );

  const onSaveSuccess = useCallback((formData, keepOpenAfterSaving, callback) => {
    if (!keepOpenAfterSaving) {
      hideModalForAddEdit();
      selectAgGridRow(formData);
    }

    if (callback) {
      return callback();
    }
  }, [hideModalForAddEdit, selectAgGridRow]);

  const { mutate: addConnection, isLoading: isAddingConnection } = useAddConnection();
  const { mutate: updateConnection, isLoading: isUpdatingConnection } = useUpdateConnection();

  const saveConnection: SaveEntityFunction<ConnectionFormData> = useCallback(
    async (formData, openForEdit, { keepOpenAfterSaving, callback }) => {
      if (openForEdit) {
        updateConnection(
          { ...formData, periodName: period.name },
          { onSuccess: () => onSaveSuccess(formData, keepOpenAfterSaving, callback) }
        );
      } else {
        addConnection(
          { ...formData, periodName: period.name },
          { onSuccess: () => onSaveSuccess(formData, keepOpenAfterSaving, callback) }
        );
      }
    }, [onSaveSuccess, addConnection, updateConnection, period]
  );

  const isSavingConnection = isAddingConnection || isUpdatingConnection;

  const {
    onAddJobError,
    onAddJobSuccess,
    onJobComplete,
    jobId,
  } = useGenerateJournalJob();

  const addJob = useAddJob({
    jobType: Config.jimJobs.generateJournalFile.type,
    onError: onAddJobError,
    onSuccess: onAddJobSuccess,
  });

  const generateJournal = async () => {
    if ((!connection?.connName || !calculationId?.value)) {
      errorNotification('Please select both Connection Name and Calculation ID.');
      return;
    }

    const retVal = await generateJournalPayload({
      connName: connection.connName, periodName: period.name,
      calcId: calculationId?.value,
    });
    const displayName = retVal.split('/').pop() || '';
    const jobParams = ({
      clientId: lastClientId.toString(),
      connectionName: connection.connName,
      period: period.name,
      calcId: calculationId.value.toString(),
      displayName,
    });

    addJob({
      jobParams: JSON.stringify(jobParams),
      displayName,
    });
  };

  const handleTrialBalanceConnectionChange = (selectedConnection: {label: string, value: string} | null) => {
    setTrialBalanceConnection(selectedConnection);
    setTrialBalanceCalculationId(null);
  };

  const handleTrialBalanceCalculationChange = (selectedCalculation: {label: string, value: string} | null) => {
    setTrialBalanceCalculationId(selectedCalculation);
  };

  React.useEffect(() => {
    if (trialBalanceConnection?.value && period.name) {
      setIsLoadingTrialBalanceCalculations(true);
      fetchTrialBalanceCalculationId(trialBalanceConnection.value, period.name)
        .then((calculationIdList: any[]) => {
          setTrialBalanceCalculations(calculationIdList);
        })
        .catch(() => {
          setTrialBalanceCalculations([]);
        })
        .finally(() => {
          setIsLoadingTrialBalanceCalculations(false);
        });
    } else {
      setTrialBalanceCalculations([]);
    }
  }, [trialBalanceConnection?.value, period.name]);

  const generateTrialBalance = async () => {
    if ((!trialBalanceConnection?.value || !trialBalanceCalculationId?.value)) {
      errorNotification('Please select both Connection Name and Calculation ID for Trial Balance.');
      return;
    }

    setIsGeneratingTrialBalance(true);
    infoNotification('Generating Trial Balance file...');

    try {
      const trialBalanceData = await fetchHistoricalTrialBalanceData(Number(trialBalanceCalculationId?.value));

      if (trialBalanceData && trialBalanceData.length > 0) {
        await downloadTrialBalanceFileXlsx({
          connName: trialBalanceConnection.value,
          clientName: lastClientId.toString(),
          periodName: period.name,
          trialBalance: trialBalanceData,
          environmentSuffix,
          shouldDisplayZero,
          hideDecimals,
          negativeMinus,
        });
        successNotification('Trial Balance file generated and downloaded successfully.');
      } else {
        infoNotification('No trial balance data available for the selected calculation.');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errorNotification(`Failed to generate trial balance: ${errorMessage}`);
    } finally {
      setIsGeneratingTrialBalance(false);
    }
  };

  return (
    <div className="client-admin-connection-tab">
      <SecondaryMenu>
        <PeriodDropdown />
        <div className="hamburger-dropdown ml-auto text-right">
          <HamburgerDropdown
            canModify={canModify}
            showModalForAdd={showModalForAdd}
            addEntityMessage="Add Connection"
            disableExporting
            disableImporting
          />
        </div>
      </SecondaryMenu>
      <div className="page">
        <div className="container-fluid">
          <TabHeader label="Connections" />
          <div className="row no-gutters page-content-container">
            <div className="col">
              <ViewGrid
                adminColumnRenderer={adminColumnRenderer}
                className="admin-import-grid"
                columnDefs={columnDefinitions}
                data={connections}
                isLoading={isLoadingConnections}
                shouldDisplayAdminColumn={canModify}
              />
            </div>
          </div>
          {generateJournalFlag && !isLoadingConnections && (
            <div className="row no-gutters page-content-container new-files">
              <div className="row no-gutters page-content-container">
                <div className="col">
                  <JimJobInfo
                    jobConfig={Config.jimJobs.generateJournalFile}
                    onJobComplete={onJobComplete}
                    showProgressBar
                    forceSelectionById
                    jobId={jobId}
                    autoMarkNotificationAsSeen={false}
                  />
                </div>
              </div>
              <div className="row no-gutters">
                <div className="col-12">
                  <SecondaryMenu>
                    <div className="force-two-rows">
                      <div className="row-section">
                        <div className="d-flex align-items-center mr-4 mb-2 section-title">
                          <h6 className="mb-0 mr-4">Generate Journal</h6>
                        </div>
                        <div className="d-flex align-items-center mr-4 mb-2 connection-section">
                          <div className="secondary-menu-label">Connection Name</div>
                          <div className="dropdown-container">
                            <ConnectionByTypeDropdown connType={connType} disabled={isLoadingConnections} />
                          </div>
                        </div>
                        <div className="d-flex align-items-center mr-4 mb-2 calculation-section">
                          <div className="secondary-menu-label">CalculationId</div>
                          <div className="dropdown-container">
                            <CalculationDropdown disabled={isLoadingConnections} />
                          </div>
                        </div>
                        <div className="mr-4 mb-2 button-section">
                          <Button
                            onClick={generateJournal}
                            kind={'primary'}
                            className="a-my-10"
                            disabled={!connection?.connName || !calculationId?.value}
                          >
                            Generate Journal
                          </Button>
                        </div>
                      </div>
                      <div className="row-section">
                        <div className="d-flex align-items-center mr-4 mb-2 section-title">
                          <h6 className="mb-0 mr-4">Generate Trial Balance</h6>
                        </div>
                        <div className="d-flex align-items-center mr-4 mb-2 connection-section">
                          <div className="secondary-menu-label">Connection Name</div>
                          <div className="dropdown-container">
                            <Select
                              className="select-connection"
                              clearable={false}
                              name="trial-balance-connection"
                              options={connectionsByType || []}
                              labelKey="label"
                              onChange={handleTrialBalanceConnectionChange}
                              disabled={isLoadingConnections}
                              scrollMenuIntoView={false}
                              value={trialBalanceConnection?.value || ''}
                              virtualized
                            />
                          </div>
                        </div>
                        <div className="d-flex align-items-center mr-4 mb-2 calculation-section">
                          <div className="secondary-menu-label">CalculationId</div>
                          <div className="dropdown-container">
                            <Select
                              className="select-job-id"
                              clearable={false}
                              name="trial-balance-calculation"
                              options={trialBalanceCalculations.map(calc => ({
                                label: `${calc.calcJobId} - ${renderDate(calc.dateCreated)}`,
                                value: calc.calcJobId,
                              }))}
                              labelKey="label"
                              onChange={handleTrialBalanceCalculationChange}
                              disabled={
                                isLoadingConnections
                                || !trialBalanceConnection?.value
                                || isLoadingTrialBalanceCalculations
                              }
                              scrollMenuIntoView={false}
                              value={trialBalanceCalculationId}
                              virtualized
                            />
                          </div>
                        </div>
                        <div className="mb-2 button-section">
                          <Button
                            onClick={generateTrialBalance}
                            kind={'primary'}
                            className="a-my-10"
                            disabled={
                              isGeneratingTrialBalance
                              || !trialBalanceConnection?.value
                              || !trialBalanceCalculationId?.value
                            }
                          >
                            {isGeneratingTrialBalance ? 'Generating...' : 'Download Trial Balance'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </SecondaryMenu>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {
        canModify && <AddConnectionModal
          entityToEdit={entityToEdit}
          hideModal={hideModalForAddEdit}
          isVisible={isAddEditModalVisible}
          openForEdit={Boolean(entityToEdit)}
          connections={connections}
          lookUpData={lookUpData}
          saveEntity={saveConnection}
          isSavingInProgress={isSavingConnection}
          isBusy={isLoadingLookUpData}
        />
      }
    </div>
  );
};

ConnectionsTab.propTypes = {
  period: PeriodPropTypes.isRequired,
  canModify: PropTypes.bool.isRequired,
  calculationId: CalculationIdPropTypes.isRequired,
  generateJournalFlag: PropTypes.bool.isRequired,
};

const mapStateToProps = (
  state: {
    shared: { period: Period, connection: ConnectionType, calculationId: CalculationIdType },
    user: { user: { lastClientId: string, sso: string } }
  }) => ({
  period: periodOrCurrentSelector(state),
  connection: connectionByTypeSelector(state),
  calculationId: calculationSelector(state),
  connectionsByType: connectionsByTypeOptionsSelector(state),
  lastClientId: state.user.user.lastClientId,
  sso: state.user.user.sso,
  generateJournalFlag: generateJournalFlagSelector(state),
  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),
});

export default connect(mapStateToProps)(ConnectionsTab);
